// Survey API Service
const API_ENDPOINT =
  "https://l9wfdhr013.execute-api.us-east-2.amazonaws.com/prod/survey";

/**
 * Organizes survey data into a structured format for the API
 * @param {Object} surveyData - Raw survey data from the form
 * @returns {Object} Organized survey data
 */
export const organizeSurveyData = (surveyData) => {
  console.log("🔧 Organizing survey data...");
  console.log("📊 Input data keys:", Object.keys(surveyData));

  const organized = {
    // Demographics
    demographics: {
      age: surveyData.age || null,
      biologicalSex: surveyData.biologicalSex || null,
      region: surveyData.location || null, // Map location to region
      ethnicity: surveyData.ethnicity || null,
    },

    // Physical Measurements
    measurements: {
      height: surveyData.height || null,
      weight: surveyData.weight || null,
      heightUnit: surveyData.heightUnit || "cm", // Default to cm since survey uses cm
      weightUnit: surveyData.weightUnit || "kg", // Default to kg since survey uses kg
    },

    // Family History
    familyHistory: {
      cancerHistory: surveyData.familyCancerHistory || [],
      diseaseHistory: surveyData.chronicDiseases || [],
    },

    // Smoking and Radiation History
    smokingHistory: {
      yearsSmoking: surveyData.smokingHistory || 0,
      cigarettesPerDay: surveyData.cigarettesPerDay || 0,
      livedWithSmoker: surveyData.livedWithSmoker || null,
    },

    // Radiation Exposure
    radiationExposure: surveyData.radiationExposure || null,

    // Lifestyle Factors
    lifestyle: {
      alcoholConsumption: surveyData.alcoholConsumption || null,
      dietHabits: surveyData.dietHabits || null,
    },

    // Contact Information
    contact: {
      email: surveyData.email || null,
    },

    // Metadata
    metadata: {
      submissionTimestamp: new Date().toISOString(),
      surveyVersion: "2.0",
      source: "omnicare-web-app",
    },
  };

  console.log("📋 Organized data structure:", organized);
  console.log("📏 Data size breakdown:", {
    total: JSON.stringify(organized).length,
    demographics: JSON.stringify(organized.demographics).length,
    measurements: JSON.stringify(organized.measurements).length,
    familyHistory: JSON.stringify(organized.familyHistory).length,
    smokingHistory: JSON.stringify(organized.smokingHistory).length,
    radiationExposure: JSON.stringify(organized.radiationExposure).length,
    lifestyle: JSON.stringify(organized.lifestyle).length,
    contact: JSON.stringify(organized.contact).length,
    metadata: JSON.stringify(organized.metadata).length,
  });

  return organized;
};

/**
 * Sends survey data to the API endpoint
 * @param {Object} surveyData - Raw survey data
 * @returns {Promise<Object>} API response
 */
export const submitSurveyData = async (surveyData) => {
  try {
    console.log("🚀 Starting survey data submission...");
    console.log("📊 Raw survey data received:", surveyData);

    const organizedData = organizeSurveyData(surveyData);
    console.log("📋 Organized data structure:", organizedData);
    console.log(
      "📏 Data size:",
      JSON.stringify(organizedData).length,
      "characters"
    );

    console.log("🌐 Sending POST request to:", API_ENDPOINT);
    const response = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(organizedData),
    });

    console.log("📡 Response received:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ API Error Response:", errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log("✅ API Success Response:", result);

    return {
      success: true,
      data: result,
      message: "Survey data submitted successfully",
    };
  } catch (error) {
    console.error("💥 Error submitting survey data:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
    return {
      success: false,
      error: error.message,
      message: "Failed to submit survey data",
    };
  }
};

/**
 * Validates that required survey data is present
 * @param {Object} surveyData - Survey data to validate
 * @returns {Object} Validation result
 */
export const validateSurveyData = (surveyData) => {
  console.log("🔍 Validating survey data...");
  console.log("📊 Available fields:", Object.keys(surveyData));
  console.log("📋 Field values:", surveyData);

  const requiredFields = ["age", "biologicalSex", "height", "weight"];

  const missingFields = requiredFields.filter((field) => !surveyData[field]);

  console.log("✅ Required fields check:", {
    required: requiredFields,
    missing: missingFields,
    present: requiredFields.filter((field) => surveyData[field]),
  });

  return {
    isValid: missingFields.length === 0,
    missingFields,
    message:
      missingFields.length > 0
        ? `Missing required fields: ${missingFields.join(", ")}`
        : "Survey data is valid",
  };
};
